import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * AdminSbtStats — edits the 4 stat counters shown below the hero
 * slider on the SBT marketing website. Values are persisted server-
 * side in `sbt_site_stats` and read by the Astro StatsPills component
 * on every homepage load (so changes go live immediately, no rebuild).
 */
export default function AdminSbtStats() {
  const [form, setForm] = useState({ campaigns: 0, leads: 0, clients: 0, owned_media: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sbt-site/stats");
      setForm({
        campaigns: r.data.campaigns ?? 0,
        leads: r.data.leads ?? 0,
        clients: r.data.clients ?? 0,
        owned_media: r.data.owned_media ?? 0,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load stats");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => {
    const n = parseInt(e.target.value || "0", 10);
    setForm((f) => ({ ...f, [k]: Number.isFinite(n) ? Math.max(0, n) : 0 }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/sbt-site/stats", form);
      toast.success("Marketing site stats updated. Live on the homepage now.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500" data-testid="sbt-stats-loading">Loading…</div>;

  const fields = [
    { key: "campaigns",   label: "Campaigns",   hint: "Total campaigns delivered" },
    { key: "leads",       label: "Leads",       hint: "Base value — live ticker adds +1 every 2s on the site" },
    { key: "clients",     label: "Clients",     hint: "Active client accounts" },
    { key: "owned_media", label: "Owned Media", hint: "Number of owned media properties" },
  ];

  return (
    <div className="space-y-6" data-testid="sbt-stats-tab">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Marketing Website — Stat Counters</h3>
        <p className="text-sm text-slate-500 mt-1">
          Edit the 4 numbers shown in the cyan pills below the homepage hero on
          sbtresearch.com. Changes apply immediately — no redeploy required.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key} className="space-y-1">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{f.label}</label>
            <Input
              type="number"
              min="0"
              value={form[f.key]}
              onChange={set(f.key)}
              data-testid={`sbt-stats-input-${f.key}`}
              className="rounded-sm"
            />
            <p className="text-xs text-slate-400">{f.hint}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Button onClick={save} disabled={saving} data-testid="sbt-stats-save-btn" className="rounded-sm">
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button variant="outline" onClick={load} disabled={saving} data-testid="sbt-stats-reload-btn" className="rounded-sm">
          Reload from server
        </Button>
      </div>
    </div>
  );
}
